/**
 * The CSP's cs-analysis sign-out (CT-15747; cs-analysis
 * docs/design-chat-and-access.md "Staff sign-out").
 *
 * Every CSP sign-out calls this first, then infurnia-main's `user/logout` as
 * before. The api's POST /api/v1/auth/sign-out clears the `cs-analysis-session`
 * cookie (and the client tier's), so the next person to sign in on this
 * browser does not go on with this person's cs-analysis session.
 *
 * It never fails and never holds the logout past `timeout_ms`: a network
 * error, any status (a 404 while the route is not deployed yet) or no answer
 * in time all resolve, with a word for what happened ('cleared',
 * 'status_<n>', 'failed', 'timeout', 'skipped').
 *
 * Its own fetch, on purpose outside cs_analysis_fetch's gate (the one other
 * request to cs-analysis allowed one; cs_analysis_requests_guard.test.js):
 * Logout must reach auth/sign-out even after a refused sign-in, when the gate
 * fails every call at once and sends nothing, and it must never sign in first
 * (the gate's page-load check could) only to clear that session again.
 */
export const CS_ANALYSIS_SIGN_OUT_PATH = '/api/v1/auth/sign-out';

/** How long a sign-out waits for cs-analysis before going on with infurnia-main's logout. */
export const CS_ANALYSIS_SIGN_OUT_TIMEOUT_MS = 1500;

export function cs_analysis_sign_out({ timeout_ms = CS_ANALYSIS_SIGN_OUT_TIMEOUT_MS, fetch_fn } = {}) {
    const base = (global.config && global.config.cs_analysis_path) || '';
    const do_fetch = fetch_fn || (typeof fetch === 'function' ? fetch : null);
    if (!base || !do_fetch) return Promise.resolve('skipped');
    const url = base.replace(/\/+$/, '') + CS_ANALYSIS_SIGN_OUT_PATH;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;

    return new Promise((resolve) => {
        const timer = setTimeout(() => {
            if (controller) controller.abort();
            resolve('timeout');
        }, timeout_ms);
        const done = (word) => {
            clearTimeout(timer);
            resolve(word);
        };
        let call;
        try {
            call = do_fetch(url, {
                method: 'POST',
                // The api's CSRF rule for this route (the guard's header and value).
                headers: { 'X-Requested-With': 'cs-analysis-csp' },
                credentials: 'include',
                ...(controller ? { signal: controller.signal } : {}),
            });
        } catch (_) {
            done('failed');
            return;
        }
        Promise.resolve(call).then(
            (resp) => done(resp && resp.status === 204 ? 'cleared' : 'status_' + (resp ? resp.status : 'none')),
            () => done('failed'),
        );
    });
}
